import { useMemo } from 'react';
import Button from '@/components/Button/Button';
import Select from '@/components/Select/Select';
import {
    ChevronFirst,
    ChevronLast,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';

const Pagination = ({
    page,
    pageSize = 10,
    pageSizes = [10, 20, 30, 50, 100],
    total,
    loading,
    onPageChange,
    onPageSizeChange,
}) => {
    const totalPages = Math.ceil(total / pageSize);

    const visiblePages = useMemo(() => {
        if (totalPages < 4)
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (page === 1) return [1, 2, 3];
        if (page === totalPages)
            return [totalPages - 2, totalPages - 1, totalPages];
        return [page - 1, page, page + 1];
    }, [page, totalPages]);

    const handlePrev = () => {
        if (page > 1) onPageChange(page - 1);
    };

    const handleNext = () => {
        if (page < totalPages) onPageChange(page + 1);
    };

    const handleFirst = () => {
        if (page > 1) onPageChange(1);
    };
    const handleLast = () => {
        if (page < totalPages) onPageChange(totalPages);
    };

    return (
        <div className="flex flex-wrap items-center justify-between p-4 border-t bg-white gap-2">
            <div className="flex items-center gap-2">
                <span className="text-nowrap text-sm text-secondary">
                    showing
                </span>
                <Select
                    compact
                    options={pageSizes.map((n) => ({ value: n, text: n }))}
                    labelKey="text"
                    inputValue={pageSize}
                    onChange={(val) => onPageSizeChange(Number(val))}
                />

                <span className="text-nowrap text-sm text-secondary">
                    of {total >= pageSize ? total : 'all '} results{' '}
                    {total < pageSize && `(${total} items)`}
                </span>
            </div>

            <div className="flex items-center gap-2">
                <Button
                    loading={loading}
                    variant="secondary"
                    appearance="outline"
                    size="xs"
                    disabled={page === 1}
                    onClick={handleFirst}
                >
                    <ChevronFirst size={18} />
                </Button>
                <Button
                    loading={loading}
                    variant="secondary"
                    appearance="outline"
                    size="xs"
                    disabled={page === 1}
                    onClick={handlePrev}
                >
                    <ChevronLeft size={18} />
                </Button>
                {page > 2 && (
                    <span className="text-sm text-secondary">...</span>
                )}
                {visiblePages.map((p) => (
                    <Button
                        loading={loading}
                        variant="secondary"
                        size="xs"
                        key={p}
                        appearance={p === page ? 'solid' : 'outline'}
                        onClick={() => onPageChange(p)}
                        className={p === page ? 'bg-blue-600 text-white' : ''}
                    >
                        {p}
                    </Button>
                ))}
                {page < totalPages - 1 && (
                    <span className="text-sm text-secondary">...</span>
                )}
                <Button
                    loading={loading}
                    variant="secondary"
                    appearance="outline"
                    size="xs"
                    disabled={page === totalPages}
                    onClick={handleNext}
                >
                    <ChevronRight size={18} />
                </Button>
                <Button
                    loading={loading}
                    variant="secondary"
                    appearance="outline"
                    size="xs"
                    disabled={page === totalPages}
                    onClick={handleLast}
                >
                    <ChevronLast size={18} />
                </Button>
            </div>
        </div>
    );
};

export default Pagination;
